import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { MaterialModule } from './shared/material/material.module';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { MatNativeDateModule } from '@angular/material/core';
import { userReducer } from './core/reducers/user/user.reducer';

import { LoginPageModule } from './pages/login-page/login-page.module';
import { LoginEffects } from './core/effects/user/user.effects';

import { TicketInteractionEffects } from './core/effects/ticket/ticket-interaction.effects';
import { TicketEffects } from './core/effects/ticket/ticket.effects';
import { ticketReducer } from './core/reducers/ticket/ticket.reducer';

import { FilesEffects } from './core/effects/files/files.effects';
import { filesReducer } from './core/reducers/files/files.reducer';

import { StepperEffects } from './core/effects/stepper/stepper.effects';
import { stepperReducer } from './core/reducers/stepper/stepper.reducer';

import { GlobalSearchEffects } from './core/effects/global-search/global-search.effects';
import { globalSearchReducer } from './core/reducers/global-search/global-search.reducer';

import { NotificationModule } from './shared/components/notification/notification.module';

import { AnnouncementsEffects } from './core/effects/announcements/announcements.effects';
import { announcementsReducer } from './core/reducers/announcements/announcements.reducers';

import { ReportsEffects } from './core/effects/reports/reports.effects';
import { reportsReducer } from './core/reducers/reports/reports.reducers';

import { CategoriesEffects } from './core/effects/categories/categories.effects';
import { categoriesReducer } from './core/reducers/categories/categories.reducer';

import { animationReducer } from './core/reducers/animations/animations.reducer';

import { RequestAccessPageModule } from './pages/request-access-page/request-access-page.module';
import { requestAccessReducer } from './core/reducers/request-access/request-access.reducer';
import { RequestAccessEffects } from './core/effects/request-access/request-access.effects';

import { changePasswordReducer } from './core/reducers/change-password/change-password.reducer';
import { ChangePasswordEffects } from './core/effects/change-password/change-password.effects';

import { ForgotPasswordPageModule } from './pages/forgot-password-page/forgot-password-page.module';
import { forgotPasswordReducer } from './core/reducers/forgot-password/forgot-password.reducer';
import { ForgotPasswordEffects } from './core/effects/forgot-password/forgot-password.effects';
import { AppInitService } from './core/services/init-service/app-init.service';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { ReportsPageModule } from './pages/reports/reports-page.module';

export function translateHttpLoaderFactory(httpBackend: HttpBackend): TranslateHttpLoader {
  return new TranslateHttpLoader(new HttpClient(httpBackend), './assets/i18n/');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MaterialModule,
    NotificationModule,
    MatNativeDateModule,

    // pages
    LoginPageModule,
    RequestAccessPageModule,
    ForgotPasswordPageModule,
    ReportsPageModule,

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpBackend]
      }
    }),
    StoreModule.forRoot({
      globalSearch: globalSearchReducer,
      requestAccess: requestAccessReducer,
      changePassword: changePasswordReducer,
      forgotPassword: forgotPasswordReducer,
      stepper: stepperReducer,
      ticket: ticketReducer,
      user: userReducer,
      files: filesReducer,
      categories: categoriesReducer,
      announcements: announcementsReducer,
      animations: animationReducer,
      reports: reportsReducer
    }),
    EffectsModule.forRoot([
      GlobalSearchEffects,
      RequestAccessEffects,
      ChangePasswordEffects,
      ForgotPasswordEffects,
      LoginEffects,
      StepperEffects,
      CategoriesEffects,
      TicketEffects,
      TicketInteractionEffects,
      FilesEffects,
      AnnouncementsEffects,
      ReportsEffects
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 100,
      logOnly: environment.production
    })
  ],
  providers:[
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: AppInitService) => () => configService.loadConfiguration().toPromise(),
      deps: [AppInitService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
